import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useLocation, useNavigate } from 'react-router-dom';
import { DrawerTabs } from '~/components/common/Tabs';
import { isObjectEqual, getCountryCodeFromPhone } from '~/utils/common';
import {
  DriverProfileSchema,
  DriverVehiclesSchema,
} from '~/utils/schema/driver';
import { ICustomerSupport, VehicleType } from '~/models/driver';
import { fromQueryString, omitFromQueryString } from '~/utils/queryString';
import { alertParams, errorAlert, showAlert } from '~/components/common/Alert';
import { IAlert } from '~/components/common/Alert/Alert';
import { AdminOptionTabs } from '~/models/adminOptions';
import themes, { styled } from '~/themes';
import Tutorial from '~/components/common/Tutorial';
import { TutorialData } from '~/constants/tutorial';
import { cloneDeep, omit } from 'lodash';
import { CountryCode, parsePhoneNumber } from 'libphonenumber-js';
import { useAuth } from '~/providers/AuthProvider';
import { queryClient } from '~/lib/react-query';
import { useKeyDow } from '~/hooks/useKeyDown';
import { useFirstLoadingDetection } from '~/hooks';
import { Icon } from '~/components/common/Icon';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { ElementSelect } from '~/components/common/ElementSelect/ElementSelect';
import moment from 'moment';
import Vehicles from './components/Vehicles';
import Profile from './components/Profile';
import { DriverAvatar } from './components/DriverAvatar';
import { Timeline } from './components/Timeline';
import {
  driverSettingsQueryKeys,
  useDeleteDriver,
  useGetDriverDetailQuery,
  useResendInvitationDriver,
  useUpdateDriver,
} from '../apis';
import { IDriver } from '../types';
import {
  DEFAULT_DELIVERY,
  DRIVER_DOCS,
  DRIVER_DRAWER_TABS,
  DRIVER_DRAWER_TAB_VALUE,
} from '../constants';
import Document from './components/Document';
import { ShiftSchedule } from './components/ShiftSchedule';
import ActionBar from '../../components/ActionBar';

const StyledDetailContainer = styled(Stack)(() => ({
  'position': 'relative',
  'height': '100%',
  'flexDirection': 'column',
  'boxSizing': 'border-box',
  '.save-change-btn': {
    'position': 'absolute',
    'bottom': 0,
    'left': 0,
    'right': 0,
    'borderRadius': 0,
    'minHeight': 50,
    '&.loading': {
      opacity: 1,
      background: '#FED1C7 !important',
    },
  },
}));

interface IDriverDetailProps {}

export const DriverDetail: React.FC<IDriverDetailProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { account, getTooltipOpen, updateUserTooltip } = useAuth();

  const { search, page, sort, id: driverId } = fromQueryString(location.search);
  const [tab, setTab] = useState(DRIVER_DRAWER_TAB_VALUE.PROFILE);

  const { data, isFetching } = useGetDriverDetailQuery({
    id: driverId,
  });

  const isFirstLoading = useFirstLoadingDetection([isFetching]);

  const { mutate: updateDriver, isLoading: isLoadingUpdate } = useUpdateDriver({
    onSuccess: () => {
      queryClient.invalidateQueries(
        driverSettingsQueryKeys.getDriverDetail({ id: driverId }),
      );
      queryClient.invalidateQueries(driverSettingsQueryKeys.getDriverList());
      showAlert(alertParams.success);
    },
  });

  const { mutate: resendInvitation } = useResendInvitationDriver({
    onSuccess: () => {
      showAlert({
        ...alertParams.success,
        title: 'Resent Invitation Successfully',
        description:
          'The invitation SMS has been resent to the driver’s phone.',
      });
    },
    onError: (error) => {
      showAlert(errorAlert(error));
    },
  });

  const { mutate: deleteDriverMutation } = useDeleteDriver({
    onSuccess: () => {
      queryClient.invalidateQueries(driverSettingsQueryKeys.getDriverList());
      showAlert({
        ...alertParams.success,
        title: 'Driver was deleted successfully',
        onOk: () => {
          navigate(`../setting/${AdminOptionTabs.Drivers}`);
        },
        onCancel: () => {
          navigate(`../setting/${AdminOptionTabs.Drivers}`);
        },
      });
    },
  });

  const handleChangeTab = useCallback((value: string) => {
    setTab(value);
  }, []);

  useEffect(() => {
    handleChangeTab(DRIVER_DRAWER_TAB_VALUE.PROFILE);
  }, [driverId]);

  const onDeleteAlert = useCallback((id: string) => {
    let params: IAlert = {
      ...alertParams.warning,
      title: 'Warning',
      cancelText: 'Cancel',
      okText: 'Yes, Delete',
      onOk: () => deleteDriverMutation({ id }),
    };
    params = {
      ...params,
      description:
        'You are about to delete this driver. This will affect tasks with the driver assigned. Are you sure you want to continue?',
    };
    showAlert(params);
  }, []);

  const checkFormChange = (formData, resData) => {
    if (
      formData.customer_support === resData.customer_support &&
      formData.customer_support === ICustomerSupport.ContactAlternativePhone
    ) {
      return isObjectEqual(formData, resData);
    }
    return isObjectEqual(
      omit(formData, ['alternative_phone']),
      omit(resData, ['alternative_phone']),
    );
  };

  const handleCloseDriverDetail = () => {
    navigate({
      pathname: location.pathname,
      search: omitFromQueryString(location.search, 'id'),
    });
  };

  useKeyDow({
    targetKey: 'Escape',
    disabled: !driverId,
    callback: handleCloseDriverDetail,
  });

  const headerComponent = useMemo(
    () => (
      <Stack
        sx={{
          pt: 2.4,
          pb: 1.2,
          px: 2,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 0.8,
          }}
        >
          <DriverAvatar initAvatar={data?.avatar} rounded />
          <Typography
            data-testid='display-name-driver-detail'
            sx={{
              color: themes.color.violet500,
              fontSize: 18,
              fontWeight: '600',
            }}
          >
            {data?.display_name}
          </Typography>
        </Box>
        <Stack direction='row' spacing={0.6} maxHeight={themes.spacing(3)}>
          <ElementSelect
            paperProps={{
              sx: {
                'width': 145,
                'borderRadius': '10px',
                '.MuiButtonBase-root': {
                  padding: '6px',
                },
              },
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            elementSelect={() => (
              <MoreHorizIcon
                sx={{ fontSize: 20, color: themes.color.violet900 }}
              />
            )}
            iconButtonProps={{
              sx: { height: 26, width: 26 },
            }}
            onChange={(o) => {
              if (o.value === 'delete') {
                onDeleteAlert(data?.id);
              }
              if (o.value === 'resend') {
                resendInvitation({ id: data?.id });
              }
            }}
            options={[
              ...(data?.status === 'pending'
                ? [
                    {
                      label: 'Resend invitation',
                      value: 'resend',
                    },
                  ]
                : []),
              ...(driverId
                ? [
                    {
                      label: 'Delete driver',
                      value: 'delete',
                    },
                  ]
                : []),
            ]}
          />
          <IconButton onClick={() => handleCloseDriverDetail()}>
            <Icon name='close' size={10} color={themes.bg.darkPurple} />
          </IconButton>
        </Stack>
      </Stack>
    ),
    [data],
  );

  const tabComponent = useMemo(
    () => (
      <Tutorial
        isOpen={driverId && getTooltipOpen(TutorialData.ManageDriver.id)}
        onClose={() =>
          updateUserTooltip({
            [TutorialData.ManageDriver.id]: true,
          })
        }
        delay={300}
        {...TutorialData.ManageDriver}
      >
        <Box
          sx={{
            '& .MuiTab-root': {
              px: '0px !important',
            },
            '& .tab-container': {
              gap: 2.4,
              px: 2,
            },
            [themes.breakpoints.down('md')]: {
              '& .tab-container': {
                width: 'max-content',
                minWidth: '100%',
              },
            },
          }}
        >
          <DrawerTabs
            tabs={DRIVER_DRAWER_TABS}
            value={tab}
            onChange={handleChangeTab}
            lowerCaseLabel
            variant='scrollable'
            containerClassName='tab-container'
          />
        </Box>
      </Tutorial>
    ),
    [tab, data, search, page, sort, driverId, account],
  );

  const tabContentComponent = useMemo(() => {
    if (isFirstLoading)
      return (
        <Stack alignItems='center'>
          <CircularProgress size={20} />
        </Stack>
      );
    switch (tab) {
      case DRIVER_DRAWER_TAB_VALUE.PROFILE:
        return <Profile />;
      case DRIVER_DRAWER_TAB_VALUE.SHIFT:
        return <ShiftSchedule />;
      case DRIVER_DRAWER_TAB_VALUE.VEHICLES:
        return <Vehicles />;
      case DRIVER_DRAWER_TAB_VALUE.DOCS:
        return <Document />;
      case DRIVER_DRAWER_TAB_VALUE.TIMELINES:
        return <Timeline />;
      default:
        return <Profile />;
    }
  }, [tab, isFirstLoading]);

  const handleSubmit = (d: any) => {
    const values: IDriver = cloneDeep(d);
    switch (tab) {
      case DRIVER_DRAWER_TAB_VALUE.VEHICLES:
        Object.keys(values.vehicle).forEach((k) => {
          if (!values.vehicle[k]) {
            values.vehicle[k] = null;
          }
        });
        updateDriver({
          vehicle: {
            ...values.vehicle,
            type: VehicleType.CAR,
          },
          id: data?.id,
        });
        break;
      case DRIVER_DRAWER_TAB_VALUE.PROFILE:
      default:
        values.id = data?.id;
        if (values.dob) {
          values.dob = moment(values.dob, 'MM/DD/YYYY').format('YYYY-MM-DD');
        }

        Object.keys(values.address).forEach((k) => {
          if (!values.address[k]) {
            values.address[k] = null;
          }
        });
        if (
          values.customer_support === ICustomerSupport.ContactAlternativePhone
        ) {
          values.alternative_phone = parsePhoneNumber(
            values.alternative_phone,
            values.alternative_phone_country_code as CountryCode,
          ).number;
        } else {
          delete values.alternative_phone;
        }

        values.email = values.email ? values.email : null;
        values.dob = values.dob ? values.dob : null;
        delete values.vehicle;
        delete values.driverDocParams;
        updateDriver({
          ...values,
          id: data?.id,
        });
        break;
    }
  };

  const validationSchema = useMemo(() => {
    switch (tab) {
      case DRIVER_DRAWER_TAB_VALUE.VEHICLES:
        return DriverVehiclesSchema;
      case DRIVER_DRAWER_TAB_VALUE.PROFILE:
      default:
        return DriverProfileSchema;
    }
  }, [tab]);

  const initialValues = useMemo(
    () => ({
      email: data?.email,
      first_name: data?.first_name,
      last_name: data?.last_name,
      display_name: data?.display_name,
      phone: data?.phone,
      dob: data?.dob,
      address: data?.address,
      customer_support:
        data?.customer_support || ICustomerSupport.ContactDriverPhone,
      alternative_phone: data?.alternative_phone,
      alternative_phone_country_code: 'CA',
      ...(data?.alternative_phone && {
        alternative_phone: parsePhoneNumber(data?.alternative_phone)
          .nationalNumber,
        alternative_phone_country_code: getCountryCodeFromPhone(
          data?.alternative_phone,
        ),
      }),
      vehicle: data?.vehicle,
      driverDocParams: DRIVER_DOCS.reduce(
        (rs, { id }) => ({
          ...rs,
          [id]: data[id],
        }),
        { id: data?.id },
      ),
      delivery: data?.delivery || DEFAULT_DELIVERY,
    }),
    [tab, data],
  );

  return (
    <StyledDetailContainer data-testid='driver-detail-container'>
      {headerComponent}
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
        validationSchema={validationSchema}
      >
        {({ values, isValid, dirty, resetForm, submitForm }) => (
          <>
            {tabComponent}
            <Stack
              sx={{
                overflowY: 'auto',
                overflowX: 'hidden',
                height: '100%',
                padding: themes.spacing(1.6, 2, 2, 2),
              }}
              className='customized-scrollbar'
            >
              <Form>
                {tabContentComponent}
                {![
                  DRIVER_DRAWER_TAB_VALUE.DOCS,
                  DRIVER_DRAWER_TAB_VALUE.TIMELINES,
                ].includes(tab) && dirty ? (
                  <Box className='save-change-btn'>
                    <ActionBar
                      onCancel={resetForm}
                      onSave={submitForm}
                      loading={isLoadingUpdate}
                      disabled={
                        isFetching ||
                        checkFormChange(values, initialValues) ||
                        !isValid
                      }
                    />
                  </Box>
                ) : null}
              </Form>
            </Stack>
          </>
        )}
      </Formik>
    </StyledDetailContainer>
  );
};
